import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';
import { useLoader } from '../context/loaders/useLoader';
import { useToast } from '../context/toasts/useToast';
import { FileAttachment } from '../types/kanban';

export function useFileAttachments(cardId?: number) {
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [error, setError] = useState<Error | null>(null);

  const { getCardAttachments, uploadCardAttachment, deleteFile } = useApi();
  const { withLoader } = useLoader();
  const { addNotification } = useToast();

  const fetchAttachments = useCallback(async () => {
    if (!cardId) {
      return [];
    }
    try {
      setError(null);
      const data = await withLoader(() => getCardAttachments(cardId), 'Loading attachments...');
      setAttachments(data.files || []);
      return data.files || [];
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load attachments';
      setError(err instanceof Error ? err : new Error(errorMessage));
      addNotification({
        type: 'error',
        title: 'Error Loading Attachments',
        message: errorMessage,
        duration: 5000,
      });
      return [];
    }
  }, [getCardAttachments, cardId, withLoader, addNotification]);

  const uploadFile = useCallback(
    async (file: File) => {
      if (!cardId) {
        return null;
      }
      try {
        setError(null);

        // Validate file size (10MB for images, 50MB for documents)
        const maxSize = file.type.startsWith('image/') ? 10 * 1024 * 1024 : 50 * 1024 * 1024;
        if (file.size > maxSize) {
          const maxSizeMB = file.type.startsWith('image/') ? '10MB' : '50MB';
          throw new Error(`File size exceeds ${maxSizeMB} limit`);
        }

        const data = await withLoader(
          () => uploadCardAttachment(cardId, file, 'Card'),
          'Uploading file...'
        );

        // Add new attachment to state
        setAttachments((prev) => [...prev, data]);

        addNotification({
          type: 'success',
          title: 'File Uploaded',
          message: `Successfully uploaded ${file.name}`,
          duration: 3000,
        });

        return data;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to upload file';
        setError(err instanceof Error ? err : new Error(errorMessage));
        addNotification({
          type: 'error',
          title: 'Upload Failed',
          message: errorMessage,
          duration: 5000,
        });
        throw err;
      }
    },
    [uploadCardAttachment, cardId, withLoader, addNotification]
  );

  const deleteAttachment = useCallback(
    async (fileUuid: string) => {
      try {
        setError(null);
        await withLoader(() => deleteFile(fileUuid), 'Deleting file...');

        // Remove attachment from state
        setAttachments((prev) => prev.filter((a) => a.uuid !== fileUuid));

        addNotification({
          type: 'success',
          title: 'File Deleted',
          message: 'File deleted successfully',
          duration: 3000,
        });

        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete file';
        setError(err instanceof Error ? err : new Error(errorMessage));
        addNotification({
          type: 'error',
          title: 'Delete Failed',
          message: errorMessage,
          duration: 5000,
        });
        throw err;
      }
    },
    [deleteFile, withLoader, addNotification]
  );

  useEffect(() => {
    if (cardId) {
      fetchAttachments();
    }
  }, [cardId, fetchAttachments]);

  return {
    attachments,
    error, // For debugging, not for UI display
    loading: false, // Loading is handled by global loader
    refetch: fetchAttachments,
    uploadFile,
    deleteAttachment,
  };
}
